import {
  isBalanceAdjustmentTransaction,
  isCreditCardCharge,
  isExpenseForReports,
  isIncomeForReports,
  isTransferTransaction,
} from "./transaction-types";
import {
  findAccountByIdOrName,
  getTransferAccountIds,
  transactionMatchesAccount,
} from "./account-relations";
import { getTransactionAmountSign } from "./transaction-sign";
import { parseCurrency, roundMoney } from "./utils";

export const MONTH_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function endOfDay(dateValue = new Date()) {
  const date = new Date(dateValue);
  date.setHours(23, 59, 59, 999);
  return date;
}

function startOfMonth(year, month) {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

function endOfPreviousDay(dateValue) {
  const date = new Date(dateValue);
  date.setDate(date.getDate() - 1);
  date.setHours(23, 59, 59, 999);
  return date;
}

function endOfMonth(year, month) {
  return endOfDay(new Date(year, month + 1, 0));
}

function getBalanceAsOfDate(options = {}) {
  if (options.asOfDate) return options.asOfDate;

  if (Number.isInteger(options.month) && Number.isInteger(options.year)) {
    return endOfMonth(options.year, options.month);
  }

  return null;
}

function getDayKey(dateValue) {
  const date = new Date(dateValue);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function compareTransactionsByDate(a = {}, b = {}) {
  const dateDiff = new Date(a.date || 0) - new Date(b.date || 0);
  if (dateDiff !== 0) return dateDiff;
  return String(a.id || "").localeCompare(String(b.id || ""), "pt-BR");
}

function getStatementTransactionDate(transaction = {}) {
  return transaction.date;
}

function isSameMonthValue(dateValue, month, year) {
  const date = new Date(dateValue || 0);
  return date.getMonth() === month && date.getFullYear() === year;
}

export function shiftDateByDelta(dateValue, originalDateValue, nextDateValue) {
  const delta = new Date(nextDateValue).getTime() - new Date(originalDateValue).getTime();
  return new Date(new Date(dateValue).getTime() + delta);
}

export function getEffectiveTransactionDate(transaction = {}, realizadoOnly = true) {
  return realizadoOnly && transaction.status === true
    ? transaction.realized_at || transaction.date
    : transaction.date;
}

export function isTransactionInMonth(transaction = {}, month, year, realizadoOnly = true) {
  return isSameMonthValue(getEffectiveTransactionDate(transaction, realizadoOnly), month, year);
}

export function filterByStatus(transactions = [], realizadoOnly = true, options = {}) {
  if (!realizadoOnly) {
    if (!options.asOfDate) return transactions;

    const asOfDate = endOfDay(options.asOfDate);
    return transactions.filter((tx) => new Date(tx.date || 0) <= asOfDate);
  }

  if (!options.asOfDate) {
    return transactions.filter((tx) => tx.status === true);
  }

  const asOfDate = endOfDay(options.asOfDate);
  return transactions.filter((tx) => (
    tx.status === true &&
    new Date(getEffectiveTransactionDate(tx, true) || 0) <= asOfDate
  ));
}

export function filterVisibleTransactions(transactions = []) {
  return transactions.filter((tx) => !isBalanceAdjustmentTransaction(tx) && !isCreditCardCharge(tx));
}

export function isActiveAccount(account = {}) {
  return account.active !== false;
}

export function isDashboardVisibleAccount(account = {}) {
  return isActiveAccount(account) && account.showOnDashboard !== false;
}

export function getSignedTransactionAmount(transaction = {}) {
  const amount = Math.abs(parseCurrency(transaction.amount));
  return getTransactionAmountSign(transaction) === "+" ? amount : -amount;
}

export function getTransferGroupRows(transactions = [], allTransactions = transactions, realizadoOnly = true, accounts = []) {
  const rows = [];
  const handled = new Set();
  const allByTransferId = new Map();

  allTransactions
    .filter(isTransferTransaction)
    .forEach((tx) => {
      const key = tx.linked_transfer_id || tx.id;
      if (!allByTransferId.has(key)) allByTransferId.set(key, []);
      allByTransferId.get(key).push(tx);
    });

  transactions.forEach((tx) => {
    if (!isTransferTransaction(tx)) {
      rows.push(tx);
      return;
    }

    const key = tx.linked_transfer_id || tx.id;
    if (handled.has(key)) return;
    handled.add(key);

    const pair = allByTransferId.get(key) || [tx];
    const origin = pair.find((item) => item.type === "expense") || tx;
    const destination = pair.find((item) => item.type === "income");
    const amount = parseCurrency(origin?.amount ?? destination?.amount ?? tx.amount);
    const status = pair.every((item) => item.status === true);
    const realizedAt = pair.map((item) => item.realized_at).filter(Boolean).sort()[0] || null;
    const fromAccount = origin?.from_account || destination?.from_account || origin?.account || tx.from_account;
    const toAccount = origin?.to_account || destination?.to_account || destination?.account || tx.to_account;
    const { fromAccountId, toAccountId } = getTransferAccountIds(origin || tx, destination, accounts);
    const resolvedFromAccount = findAccountByIdOrName(accounts, fromAccountId, fromAccount);
    const resolvedToAccount = findAccountByIdOrName(accounts, toAccountId, toAccount);
    const fromAccountName = resolvedFromAccount?.name || fromAccount;
    const toAccountName = resolvedToAccount?.name || toAccount;

    const date = getEffectiveTransactionDate(origin || destination || tx, realizadoOnly);

    rows.push({
      ...origin,
      id: key,
      rowType: "transfer_group",
      transaction: origin,
      transferPair: pair,
      type: "transfer",
      amount,
      status,
      confirmed: status,
      realized_at: realizedAt || undefined,
      date,
      description: origin?.transfer_description || destination?.transfer_description || "Transferência",
      account: fromAccountName && toAccountName ? `${fromAccountName} -> ${toAccountName}` : origin?.account || tx.account,
      account_id: fromAccountId || origin?.account_id || tx.account_id,
      from_account: fromAccountName,
      to_account: toAccountName,
      from_account_id: fromAccountId,
      to_account_id: toAccountId,
      transaction_kind: "transfer",
      linked_transfer_id: key,
    });
  });

  return rows.sort((a, b) => {
    const dateDiff = new Date(a.date || 0) - new Date(b.date || 0);
    if (dateDiff !== 0) return dateDiff;
    return String(a.id || "").localeCompare(String(b.id || ""), "pt-BR");
  });
}

export function getAccountTransactionBalance(transactions = [], accountOrName, realizadoOnly = true, options = {}) {
  const asOfDate = getBalanceAsOfDate(options);
  const filterOptions = asOfDate ? { ...options, asOfDate } : options;
  const account = typeof accountOrName === "object"
    ? accountOrName
    : { name: accountOrName };
  const accounts = options.accounts || (account?.id || account?.name ? [account] : []);

  return filterByStatus(transactions, realizadoOnly, filterOptions)
    .filter((tx) => transactionMatchesAccount(tx, account, accounts))
    .reduce((acc, tx) => acc + getSignedTransactionAmount(tx), 0);
}

export function getAccountBalanceValue(account = {}, transactions = [], realizadoOnly = true, options = {}) {
  return parseCurrency(account.initialBalance) +
    getAccountTransactionBalance(transactions, account, realizadoOnly, options);
}

export function getAccountStatementBalanceValue(account = {}, transactions = [], realizadoOnly = true, options = {}) {
  const asOfDate = getBalanceAsOfDate(options);
  const accountScope = options.accounts || (account?.id || account?.name ? [account] : []);
  const scopedTransactions = transactions
    .filter((tx) => !realizadoOnly || tx.status === true)
    .filter((tx) => !asOfDate || new Date(getStatementTransactionDate(tx) || 0) <= endOfDay(asOfDate))
    .filter((tx) => transactionMatchesAccount(tx, account, accountScope));

  return parseCurrency(account.initialBalance) +
    scopedTransactions.reduce((acc, tx) => acc + getSignedTransactionAmount(tx), 0);
}

export function getAccountBalanceAtDate(account = {}, transactions = [], dateValue = new Date(), realizadoOnly = true) {
  return getAccountBalanceValue(account, transactions, realizadoOnly, { asOfDate: dateValue });
}

export function getAccountOpeningBalanceForMonth(account = {}, transactions = [], month, year, realizadoOnly = true, options = {}) {
  const monthStart = startOfMonth(year, month);
  return getAccountBalanceValue(account, transactions, realizadoOnly, { ...options, asOfDate: endOfPreviousDay(monthStart) });
}

export function getAccountStatementOpeningBalanceForMonth(account = {}, transactions = [], month, year, options = {}) {
  const monthStart = startOfMonth(year, month);
  return getAccountStatementBalanceValue(account, transactions, true, {
    ...options,
    asOfDate: endOfPreviousDay(monthStart),
  });
}

export function buildAccountStatementRows({
  account = {},
  transactions = [],
  month,
  year,
  realizadoOnly = true,
  transactionPredicate = null,
  accounts = [],
} = {}) {
  if (!account?.name || !Number.isInteger(month) || !Number.isInteger(year)) {
    return [];
  }

  const monthStart = startOfMonth(year, month);
  const monthEnd = endOfDay(new Date(year, month + 1, 0));
  const accountScope = accounts.length ? accounts : [account];
  const openingBalance = getAccountStatementOpeningBalanceForMonth(account, transactions, month, year, { accounts: accountScope });
  const filtered = filterByStatus(transactions, realizadoOnly)
    .filter((tx) => transactionMatchesAccount(tx, account, accountScope))
    .filter((tx) => {
      const date = new Date(getStatementTransactionDate(tx) || 0);
      return date >= monthStart && date <= monthEnd;
    })
    .sort((a, b) => {
      const dateDiff = new Date(getStatementTransactionDate(a) || 0) -
        new Date(getStatementTransactionDate(b) || 0);
      if (dateDiff !== 0) return dateDiff;
      return compareTransactionsByDate(a, b);
    });

  const rows = [{
    id: `opening-${account.id || account.name}-${year}-${month}`,
    rowType: "opening",
    date: monthStart.toISOString(),
    description: "Saldo inicial",
    account: account.name,
    balanceAfter: roundMoney(openingBalance),
  }];

  let runningBalance = openingBalance;
  let currentDayKey = null;
  let currentDayDate = null;
  let currentDayHasVisibleRows = false;

  const pushDailyBalance = () => {
    if (!currentDayKey || !currentDayHasVisibleRows) return;

    rows.push({
      id: `daily-${account.id || account.name}-${currentDayKey}`,
      rowType: "daily_balance",
      date: currentDayDate,
      description: "Saldo ao final do dia",
      account: account.name,
      balanceAfter: roundMoney(runningBalance),
    });
  };

  filtered.forEach((tx) => {
    const rowDate = getStatementTransactionDate(tx);
    const dayKey = getDayKey(rowDate);
    if (currentDayKey && dayKey !== currentDayKey) {
      pushDailyBalance();
      currentDayHasVisibleRows = false;
    }

    currentDayKey = dayKey;
    currentDayDate = rowDate;

    const previousBalance = runningBalance;
    const transactionAmount = getSignedTransactionAmount(tx);
    const affectsStatementBalance = tx.status === true;
    if (affectsStatementBalance) {
      runningBalance += transactionAmount;
    }

    if (isBalanceAdjustmentTransaction(tx)) {
      currentDayHasVisibleRows = true;
      rows.push({
        ...tx,
        rowType: "balance_adjustment",
        transaction: tx,
        date: rowDate,
        description: "Ajuste de saldo",
        previousBalance: roundMoney(previousBalance),
        transactionAmount: roundMoney(transactionAmount),
        balanceAfter: roundMoney(runningBalance),
        affectsStatementBalance,
      });
      return;
    }

    if (transactionPredicate && !transactionPredicate(tx)) return;

    currentDayHasVisibleRows = true;
    rows.push({
      ...tx,
      rowType: "transaction",
      transaction: tx,
      date: rowDate,
      previousBalance: roundMoney(previousBalance),
      transactionAmount: roundMoney(transactionAmount),
      balanceAfter: roundMoney(runningBalance),
      affectsStatementBalance,
    });
  });

  pushDailyBalance();

  return rows;
}

export function getDashboardAccountBalances(accounts = [], transactions = [], realizadoOnly = true, options = {}) {
  return accounts
    .filter(isDashboardVisibleAccount)
    .map((account) => ({
      account,
      balance: getAccountBalanceValue(account, transactions, realizadoOnly, { ...options, accounts }),
    }));
}

export function getDashboardAccountReportTotals(accounts = [], transactions = [], type = "income") {
  const matchesType = type === "expense" ? isExpenseForReports : isIncomeForReports;

  return accounts
    .filter(isDashboardVisibleAccount)
    .map((account) => ({
      account,
      value: transactions
        .filter((tx) => transactionMatchesAccount(tx, account, accounts))
        .filter(matchesType)
        .reduce((acc, tx) => acc + parseCurrency(tx.amount), 0),
    }));
}

export function getAnnualCashFlowData(transactions = [], year, realizadoOnly = true) {
  const annualMap = MONTH_LABELS.map((month, index) => ({
    month,
    monthIndex: index,
    income: 0,
    expense: 0,
  }));

  filterByStatus(transactions, realizadoOnly)
    .filter((tx) => !isTransferTransaction(tx) && !isBalanceAdjustmentTransaction(tx))
    .forEach((tx) => {
      const date = new Date(getEffectiveTransactionDate(tx, realizadoOnly));
      if (date.getFullYear() !== year) return;

      const item = annualMap[date.getMonth()];
      if (!item) return;

      const amount = parseCurrency(tx.amount);
      if (isIncomeForReports(tx)) {
        item.income += amount;
      } else if (isExpenseForReports(tx)) {
        item.expense += amount;
      }
    });

  return annualMap;
}
